import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { submitTaskToFigma, createDesignerTicket, fetchDesignsFromTicket, fetchFigmaContext, createTaskIssue, syncDesignSystem, resetTaskFigma } from '@/services/redesigns';
import { openExternal } from '@/lib/embedBridge';
import { FlowStep } from './FlowStep';
import { DesignCompare } from './DesignCompare';
import { NotesInput } from './NotesInput';
import type { Redesign, RedesignPage, RedesignTask } from '@/types';
import { useCapabilities } from '@/hooks/useCapabilities';

function humanizeRoute(route: string): string {
  return route.replace(/^\//, '').split('/').filter(Boolean)
    .map(seg => seg.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    .join(' / ') || 'Home';
}

interface FigmaFlowProps {
  redesign: Redesign;
  page: RedesignPage;
  pageIndex: number;
  task: RedesignTask;
  onBack: () => void;
}

type FigmaStep = 'capture' | 'capturing' | 'assign' | 'fetch' | 'done' | 'direct_issue';

function deriveStep(task: RedesignTask, isDesignSystem: boolean): FigmaStep {
  if (task.implementationIssue) return 'done';
  if (task.designerIssue) return 'fetch';
  if (task.figmaDesign?.status === 'complete') return 'assign';
  if (task.status === 'redesigning') return 'capturing';
  // Design system mode: allow skipping Figma and going straight to issue creation
  if (isDesignSystem && task.status === 'pending') return 'direct_issue';
  return 'capture';
}

export function FigmaFlow({ redesign, page, pageIndex, task, onBack }: FigmaFlowProps) {
  const queryClient = useQueryClient();
  const capabilities = useCapabilities();
  const redesignId = redesign._id;
  const _isDesignSystem = redesign.mode === 'design_system';
  const step = deriveStep(task, _isDesignSystem);

  const [fileKey, setFileKey] = useState('');
  const [designerGuidance, setDesignerGuidance] = useState('');
  const [manualUrls, setManualUrls] = useState('');
  const [engineerNotes, setEngineerNotes] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');


  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['redesign-tasks', redesignId] });
    queryClient.invalidateQueries({ queryKey: ['redesign', redesignId] });
  };

  const submitFigmaMutation = useMutation({
    mutationFn: (key: string) => submitTaskToFigma(redesignId, task._id, key),
    onSuccess: invalidate,
  });

  const createTicketMutation = useMutation({
    mutationFn: (params: { guidance?: string; title?: string }) =>
      createDesignerTicket(redesignId, task._id, params.guidance, params.title),
    onSuccess: invalidate,
  });

  const fetchFromTicketMutation = useMutation({
    mutationFn: () => fetchDesignsFromTicket(redesignId, task._id),
    onSuccess: (data) => {
      setTicketMessage(data.message);
      invalidate();
    },
  });

  const manualImportMutation = useMutation({
    mutationFn: (urls: string | string[]) => fetchFigmaContext(redesignId, pageIndex, urls),
    onSuccess: () => {
      setManualUrls('');
      invalidate();
    },
  });

  const syncDesignSystemMutation = useMutation({
    mutationFn: () => syncDesignSystem(redesignId, task._id),
    onSuccess: (data) => {
      setTicketMessage(`Synced ${data.variableCount} tokens. Propagation: ${data.propagationStatus}`);
      invalidate();
    },
  });

  const resetFigmaMutation = useMutation({
    mutationFn: () => resetTaskFigma(redesignId, task._id),
    onSuccess: invalidate,
  });

  const [captureWarning, setCaptureWarning] = useState('');
  const captureScreenshotMutation = useMutation({
    mutationFn: () => {
      const url = task.figmaDesign?.pageUrl;
      if (!url) throw new Error('No Figma design URL to capture');
      return fetchFigmaContext(redesignId, pageIndex, url);
    },
    onSuccess: (data) => {
      const result = data as { warning?: string };
      if (result.warning) {
        setCaptureWarning(result.warning);
      } else {
        setCaptureWarning('');
      }
      invalidate();
    },
  });

  const isDesignSystem = _isDesignSystem;

  // Editable titles for Linear issues — pre-filled with meaningful defaults
  const pageLabel = page.title || humanizeRoute(task.pageRef.route);
  const defaultDesignerTitle = isDesignSystem
    ? `[Design System Review] ${redesign.repo}`
    : `[Design Review] ${redesign.repo} — ${pageLabel}`;
  const defaultImplTitle = `[Implement] ${redesign.repo} — ${pageLabel}`;
  const [designerTicketTitle, setDesignerTicketTitle] = useState(defaultDesignerTitle);
  const [implTicketTitle, setImplTicketTitle] = useState(defaultImplTitle);

  const finalizeMutation = useMutation({
    mutationFn: (params: { notes?: string; title?: string }) =>
      createTaskIssue(redesignId, task._id, params.notes, params.title),
    onSuccess: invalidate,
  });

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, padding: 0, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 18 }}>&larr;</span> Back to pages
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>
          {page.route} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({page.viewport})</span>
        </h2>
        <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 10px', borderRadius: 10, color: '#7c3aed', background: '#f3e8ff' }}>
          Designer Review
        </span>
      </div>

      {/* Step: Direct issue creation for design_system mode (skip Figma) */}
      {step === 'direct_issue' && (
        <FlowStep stepLabel="Create Ticket">
          <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 16, lineHeight: 1.6 }}>
            The design system tokens are ready. You can either send them to a designer for review and enhancement, or directly to an engineer for implementation.
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Ticket Title
            </label>
            <input
              type="text"
              value={designerTicketTitle}
              onChange={e => { setDesignerTicketTitle(e.target.value); setImplTicketTitle(e.target.value.replace('[Design Review]', '[Implement]').replace('[Design System Review]', '[Implement]')); }}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <NotesInput
            label="Guidance / Notes"
            placeholder="e.g. Review the color palette and suggest improvements, or: Apply these tokens to globals.css..."
            value={designerGuidance}
            onChange={setDesignerGuidance}
          />

          <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
            <button
              onClick={() => createTicketMutation.mutate({ guidance: designerGuidance.trim() || undefined, title: designerTicketTitle.trim() || undefined })}
              disabled={createTicketMutation.isPending || finalizeMutation.isPending}
              style={{
                padding: '12px 28px', background: createTicketMutation.isPending ? 'var(--bg-disabled)' : '#7c3aed',
                color: 'white', border: 'none', borderRadius: 8,
                cursor: createTicketMutation.isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14,
              }}
            >
              {createTicketMutation.isPending ? 'Creating...' : 'Send to Designer'}
            </button>
            <button
              onClick={() => finalizeMutation.mutate({ notes: designerGuidance.trim() || undefined, title: implTicketTitle.trim() || undefined })}
              disabled={finalizeMutation.isPending || createTicketMutation.isPending}
              style={{
                padding: '12px 28px', background: finalizeMutation.isPending ? 'var(--bg-disabled)' : 'var(--color-success)',
                color: 'white', border: 'none', borderRadius: 8,
                cursor: finalizeMutation.isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14,
              }}
            >
              {finalizeMutation.isPending ? 'Creating...' : 'Send to Engineer'}
            </button>
          </div>

          {(createTicketMutation.isError || finalizeMutation.isError) && (
            <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
              {createTicketMutation.isError ? (createTicketMutation.error as Error).message : (finalizeMutation.error as Error).message}
            </div>
          )}

          {(createTicketMutation.isSuccess || finalizeMutation.isSuccess) && (
            <div style={{ marginTop: 12, padding: 12, background: '#f0fdf4', borderRadius: 8, color: '#166534', fontSize: 13 }}>
              Ticket created successfully
            </div>
          )}
        </FlowStep>
      )}

      {/* Step: Capture to Figma */}
      {step === 'capture' && (
        <FlowStep stepLabel="Step 1 of 3 - Capture to Figma">
          {!capabilities.figmaPush && (
            <div style={{ padding: 14, background: '#fefce8', borderRadius: 8, marginBottom: 16, color: '#854d0e', fontSize: 13 }}>
              Figma push is not available in this deployment. The Claude bridge is required for Figma capture and is only available in local development. You can still create tickets directly using "Send to Designer" or "Send to Engineer" above.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: page.originalScreenshotUrl ? '200px 1fr' : '1fr', gap: 20, alignItems: 'start', opacity: capabilities.figmaPush ? 1 : 0.5, pointerEvents: capabilities.figmaPush ? 'auto' : 'none' }}>
            {page.originalScreenshotUrl && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Current Page</div>
                <img src={page.originalScreenshotUrl} alt="Current page" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--bg-secondary)' }} />
              </div>
            )}
            <div>
              <p style={{ fontSize: 14, color: '#4b5563', margin: '0 0 14px', lineHeight: 1.6 }}>
                The live page will be captured as editable Figma frames — a pixel-perfect replica your designer can modify.
              </p>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                  Target Figma file key
                </label>
                <input
                  type="text"
                  value={fileKey}
                  onChange={e => setFileKey(e.target.value)}
                  placeholder="e.g. JhVDrwa6PuD7T61QtG8A3d"
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }}
                  autoFocus
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  From your Figma URL: figma.com/design/<strong>fileKey</strong>/...
                </div>
              </div>

              <button
                onClick={() => submitFigmaMutation.mutate(fileKey)}
                disabled={!fileKey.trim() || submitFigmaMutation.isPending}
                style={{
                  padding: '10px 24px', background: submitFigmaMutation.isPending ? 'var(--bg-disabled)' : '#7c3aed',
                  color: 'white', border: 'none', borderRadius: 8,
                  cursor: !fileKey.trim() || submitFigmaMutation.isPending ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 14,
                }}
              >
                {submitFigmaMutation.isPending ? 'Submitting...' : 'Capture to Figma'}
              </button>

              {(submitFigmaMutation.isError || (task.status === 'failed' && task.error)) && (
                <div style={{ marginTop: 10, padding: 10, background: '#fef2f2', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#dc2626' }}>
                    {submitFigmaMutation.isError ? (submitFigmaMutation.error as Error).message : task.error}
                  </span>
                  <button
                    onClick={() => resetFigmaMutation.mutate()}
                    disabled={resetFigmaMutation.isPending}
                    style={{
                      padding: '6px 14px', background: '#dc2626', color: 'white',
                      border: 'none', borderRadius: 6, cursor: 'pointer',
                      fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', marginLeft: 12,
                    }}
                  >
                    {resetFigmaMutation.isPending ? 'Resetting...' : 'Reset & Retry'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </FlowStep>
      )}

      {/* Step: Capturing (in progress) */}
      {step === 'capturing' && (
        <FlowStep stepLabel="Step 1 of 3 - Capturing">
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid #7c3aed', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 16 }} />
            <div style={{ fontSize: 16, fontWeight: 500, color: '#7c3aed' }}>Capturing page to Figma...</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>The live DOM is being serialized and sent to your Figma file.</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        </FlowStep>
      )}

      {/* Step: Assign to Designer */}
      {step === 'assign' && (
        <FlowStep stepLabel="Step 2 of 3 - Assign to Designer">
          {task.figmaDesign?.pageUrl && (
            <div style={{ padding: 14, background: '#f3e8ff', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14 }}>{'\u2705'}</span>
              <span style={{ fontSize: 14, color: '#5b21b6', fontWeight: 500 }}>Page captured in Figma</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  onClick={() => resetFigmaMutation.mutate()}
                  disabled={resetFigmaMutation.isPending}
                  style={{
                    padding: '5px 12px', background: 'transparent', color: '#7c3aed',
                    border: '1px solid #c4b5fd', borderRadius: 6, cursor: 'pointer',
                    fontWeight: 600, fontSize: 12,
                  }}
                >
                  {resetFigmaMutation.isPending ? 'Resetting...' : 'Reset & Re-push'}
                </button>
                <a href={task.figmaDesign.pageUrl} onClick={e => { e.preventDefault(); openExternal(task.figmaDesign!.pageUrl); }} style={{ color: '#7c3aed', fontWeight: 500, fontSize: 13, cursor: 'pointer' }}>
                  View in Figma &rarr;
                </a>
              </div>
            </div>
          )}

          <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 16, lineHeight: 1.6 }}>
            Create a Linear ticket for your designer. They'll review and modify the design in Figma, then post the final URLs back in the ticket.
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Ticket Title
            </label>
            <input
              type="text"
              value={designerTicketTitle}
              onChange={e => setDesignerTicketTitle(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <NotesInput
            label="Guidance for Designer"
            placeholder="e.g. Focus on the navigation layout, keep the color scheme but modernize the typography, ensure mobile-first design..."
            value={designerGuidance}
            onChange={setDesignerGuidance}
          />

          <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
            <button
              onClick={() => createTicketMutation.mutate({ guidance: designerGuidance.trim() || undefined, title: designerTicketTitle.trim() || undefined })}
              disabled={createTicketMutation.isPending}
              style={{
                padding: '12px 28px', background: createTicketMutation.isPending ? 'var(--bg-disabled)' : '#7c3aed',
                color: 'white', border: 'none', borderRadius: 8,
                cursor: createTicketMutation.isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14,
              }}
            >
              {createTicketMutation.isPending ? 'Creating...' : 'Create Designer Ticket'}
            </button>
            <button
              onClick={() => createTicketMutation.mutate({ title: designerTicketTitle.trim() || undefined })}
              disabled={createTicketMutation.isPending}
              style={{ padding: '12px 20px', background: 'transparent', color: 'var(--text-muted)', border: 'none', cursor: 'pointer', fontSize: 13 }}
            >
              Skip — No guidance
            </button>
          </div>
        </FlowStep>
      )}

      {/* Step: Fetch from Ticket */}
      {step === 'fetch' && (
        <FlowStep stepLabel="Step 3 of 3 - Review Designer's Work">
          {/* Reference: original screenshot + Figma link */}
          {(page.originalScreenshotUrl || task.figmaDesign?.pageUrl) && (
            <div style={{ marginBottom: 20, border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: page.originalScreenshotUrl && task.figmaDesign?.pageUrl ? '1fr 1fr' : '1fr', gap: 0 }}>
                {page.originalScreenshotUrl && (
                  <div style={{ borderRight: task.figmaDesign?.pageUrl ? '1px solid var(--color-border)' : 'none' }}>
                    <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Original Capture
                    </div>
                    <div style={{ padding: 8 }}>
                      <img src={page.originalScreenshotUrl} alt="Original" style={{ width: '100%', borderRadius: 6, display: 'block' }} />
                    </div>
                  </div>
                )}
                {task.figmaDesign?.pageUrl && (
                  <div>
                    <div style={{ padding: '8px 12px', background: '#faf5ff', fontSize: 11, fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{page.figmaScreenshotUrl || page.figmaImports?.some(i => i.screenshotUrl) ? 'Figma Design' : 'Pushed to Figma'}</span>
                      <a href={task.figmaDesign.pageUrl} onClick={e => { e.preventDefault(); openExternal(task.figmaDesign!.pageUrl); }} style={{ color: '#7c3aed', fontWeight: 500, fontSize: 11, cursor: 'pointer', textDecoration: 'none' }}>
                        Open in Figma &rarr;
                      </a>
                    </div>
                    <div style={{ padding: 8 }}>
                      {page.figmaScreenshotUrl ? (
                        <img src={page.figmaScreenshotUrl} alt="Figma design" style={{ width: '100%', borderRadius: 6, display: 'block' }} />
                      ) : page.figmaImports?.find(i => i.screenshotUrl) ? (
                        <img src={page.figmaImports.find(i => i.screenshotUrl)!.screenshotUrl!} alt="Figma design" style={{ width: '100%', borderRadius: 6, display: 'block' }} />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 120, gap: 12, padding: 16 }}>
                          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No screenshot captured yet</span>
                          <button
                            onClick={() => captureScreenshotMutation.mutate()}
                            disabled={captureScreenshotMutation.isPending}
                            style={{
                              padding: '8px 16px', background: '#7c3aed', color: '#fff',
                              border: 'none', borderRadius: 8, cursor: 'pointer',
                              fontWeight: 600, fontSize: 12,
                              opacity: captureScreenshotMutation.isPending ? 0.7 : 1,
                            }}
                          >
                            {captureScreenshotMutation.isPending ? 'Capturing...' : 'Capture Screenshot from Figma'}
                          </button>
                        </div>
                      )}
                      {(page.figmaScreenshotUrl || page.figmaImports?.some(i => i.screenshotUrl)) && (
                        <button
                          onClick={() => captureScreenshotMutation.mutate()}
                          disabled={captureScreenshotMutation.isPending}
                          style={{
                            marginTop: 8, padding: '4px 10px', background: 'transparent', color: '#7c3aed',
                            border: '1px solid #e9d5ff', borderRadius: 6, cursor: 'pointer',
                            fontWeight: 600, fontSize: 11, width: '100%',
                          }}
                        >
                          {captureScreenshotMutation.isPending ? 'Recapturing...' : 'Recapture Screenshot'}
                        </button>
                      )}
                      {captureScreenshotMutation.isError && (
                        <div style={{ marginTop: 8, padding: 8, background: '#fef2f2', borderRadius: 6, color: '#dc2626', fontSize: 12 }}>
                          {(captureScreenshotMutation.error as Error).message}
                        </div>
                      )}
                      {captureWarning && (
                        <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', borderRadius: 6, color: '#92400e', fontSize: 12 }}>
                          {captureWarning}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {task.designerIssue && (
            <div style={{ padding: 14, background: '#f3e8ff', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 14, color: '#5b21b6', fontWeight: 500 }}>Designer ticket:</span>
              <a href={task.designerIssue.linearIssueUrl} onClick={e => { e.preventDefault(); openExternal(task.designerIssue!.linearIssueUrl); }} style={{ color: '#7c3aed', fontWeight: 500, cursor: 'pointer' }}>
                {task.designerIssue.linearIdentifier}
              </a>
            </div>
          )}

          <p style={{ fontSize: 14, color: '#4b5563', marginBottom: 16, lineHeight: 1.6 }}>
            {isDesignSystem
              ? 'When the designer has finished updating the tokens, click below to sync them back to the design system.'
              : 'When the designer has finished, click below to fetch their Figma URLs from the ticket. Or paste URLs manually.'
            }
          </p>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            {isDesignSystem ? (
              <button
                onClick={() => syncDesignSystemMutation.mutate()}
                disabled={syncDesignSystemMutation.isPending}
                style={{
                  padding: '12px 28px', background: syncDesignSystemMutation.isPending ? 'var(--bg-disabled)' : '#1d4ed8',
                  color: 'white', border: 'none', borderRadius: 8,
                  cursor: syncDesignSystemMutation.isPending ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 14,
                }}
              >
                {syncDesignSystemMutation.isPending ? 'Syncing...' : 'Sync Tokens from Figma'}
              </button>
            ) : (
              <button
                onClick={() => fetchFromTicketMutation.mutate()}
                disabled={fetchFromTicketMutation.isPending}
                style={{
                  padding: '12px 28px', background: fetchFromTicketMutation.isPending ? 'var(--bg-disabled)' : '#7c3aed',
                  color: 'white', border: 'none', borderRadius: 8,
                  cursor: fetchFromTicketMutation.isPending ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: 14,
                }}
              >
                {fetchFromTicketMutation.isPending ? 'Fetching...' : 'Fetch Designs from Ticket'}
              </button>
            )}
          </div>

          {ticketMessage && (
            <div style={{ padding: 12, background: '#f5f3ff', borderRadius: 8, color: '#5b21b6', marginBottom: 16, fontSize: 13 }}>
              {ticketMessage}
            </div>
          )}
          {fetchFromTicketMutation.isError && (
            <div style={{ padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626', marginBottom: 16, fontSize: 13 }}>
              {(fetchFromTicketMutation.error as Error).message}
            </div>
          )}

          {/* Manual URL import */}
          <details style={{ marginBottom: 20 }}>
            <summary style={{ fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', marginBottom: 8 }}>
              Or paste Figma URLs manually
            </summary>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <textarea
                value={manualUrls}
                onChange={e => setManualUrls(e.target.value)}
                placeholder={"Paste Figma URL(s) — one per line\nhttps://www.figma.com/design/abc123/MyFile?node-id=1-23"}
                rows={3}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, fontFamily: 'monospace', resize: 'vertical', outline: 'none' }}
              />
            </div>
            <button
              onClick={() => {
                const urls = manualUrls.split('\n').map(u => u.trim()).filter(Boolean);
                manualImportMutation.mutate(urls.length === 1 ? urls[0] : urls);
              }}
              disabled={!manualUrls.trim() || manualImportMutation.isPending}
              style={{ marginTop: 8, padding: '8px 20px', background: manualImportMutation.isPending ? 'var(--bg-disabled)' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontSize: 13 }}
            >
              {manualImportMutation.isPending ? 'Importing...' : 'Import'}
            </button>
          </details>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Implementation Ticket Title
            </label>
            <input
              type="text"
              value={implTicketTitle}
              onChange={e => setImplTicketTitle(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          <NotesInput
            label="Notes for Engineer"
            placeholder="e.g. Use the existing CardGrid component, the Figma design uses spacing-lg tokens..."
            value={engineerNotes}
            onChange={setEngineerNotes}
          />

          <div style={{ marginTop: 20 }}>
            <button
              onClick={() => finalizeMutation.mutate({ notes: engineerNotes.trim() || undefined, title: implTicketTitle.trim() || undefined })}
              disabled={finalizeMutation.isPending}
              style={{
                padding: '12px 28px', background: finalizeMutation.isPending ? 'var(--bg-disabled)' : 'var(--color-success)',
                color: 'white', border: 'none', borderRadius: 8,
                cursor: finalizeMutation.isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14,
              }}
            >
              {finalizeMutation.isPending ? 'Finalizing...' : 'Finalize Ticket for Implementation'}
            </button>
          </div>

          {finalizeMutation.isError && (
            <div style={{ marginTop: 12, padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
              {(finalizeMutation.error as Error).message}
            </div>
          )}
        </FlowStep>
      )}

      {/* Step: Done */}
      {step === 'done' && (
        <FlowStep stepLabel="Complete">
          <DesignCompare
            beforeUrl={page.originalScreenshotUrl}
            beforeLabel="Before"
            afterUrl={page.figmaScreenshotUrl || page.figmaImports?.[0]?.screenshotUrl}
            afterLabel="After (Designer)"
          />

          <div style={{ padding: 16, background: '#ecfdf5', borderRadius: 8, border: '1px solid #a7f3d0' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#065f46', marginBottom: 4 }}>Implementation Ticket Ready</div>
            <a
              href={task.implementationIssue!.linearIssueUrl}
              onClick={e => { e.preventDefault(); openExternal(task.implementationIssue!.linearIssueUrl); }}
              style={{ color: '#065f46', fontWeight: 500, cursor: 'pointer', fontSize: 14 }}
            >
              {task.implementationIssue!.linearIdentifier}
            </a>
          </div>
        </FlowStep>
      )}
    </div>
  );
}
